import React, { useMemo, useState } from 'react'

import { styled, useStyleConfig, vui, VuiComponent } from '@veracity/vui'
import { T } from '@veracity/vui'
import { ChangeEvent, cs, filterUndefined, isString } from '@veracity/vui'
import { selectStateMapping } from './consts'
import { SelectProvider } from './context'
import { getInitialCount } from './helpers'
import { SelectProps } from './select.types'
import SelectIcon from './selectIcon'
import SelectSelect from './selectSelect'

export const SelectBase = styled.divBox`
	align-items: center;
	background-color: white;
	border-radius: md;
	border-width: 1px;
	display: flex;
	flex-shrink: 0;
	outline: none;
	position: relative;
	transition-duration: fast;
	width: 100%;

	&[aria-disabled='true'] {
		background-color: disabled.bg;
		color: disabled.color;
		cursor: not-allowed;
	}
`

/**
 * Displays an select element wrapped in a div to allow extra content, like counter, side icons or buttons.
 * Forwards many relevant props to the inner select. Handles different states, like loading or error.
 * Exposes some props to the children via context.
 */
export const Select = vui<'div', SelectProps>((props, ref) => {
	const [count, setCount] = useState(getInitialCount(props))
	const {
		autoComplete,
		autoFocus,
		children,
		className,
		colorScheme,
		defaultValue,
		disabled,
		iconLeft,
		iconRight,
		id,
		select,
		selectProps,
		selectRef,
		isInvalid,
		itemLeft,
		itemRight,
		max,
		maxLength,
		min,
		name,
		onBlur,
		onChange: onChangeProp,
		onFocus,
		pattern,
		placeholder,
		readOnly,
		required,
		showCount,
		size,
		state = '',
		stateMapping,
		step,
		type = 'text',
		value,
		variant,
		...rest
	} = props
	const states = { ...selectStateMapping, ...stateMapping }
	const computedColorScheme = filterUndefined({
		colorScheme: colorScheme ?? (isInvalid ? 'red' : undefined) ?? states[state]?.colorScheme
	})

	const styles = useStyleConfig('Select', { ...computedColorScheme, ...props })

	const context = useMemo(
		() => filterUndefined({ colorScheme, disabled, size, variant }),
		[colorScheme, disabled, size, variant]
	)

	function onChange(e: ChangeEvent<HTMLSelectElement>) {
		setCount(e.target.value.length)
		onChangeProp?.(e)
	}

	const aliasedProps = filterUndefined({
		'aria-disabled': disabled,
		bg: readOnly ? 'grey.10' : undefined,
		focusWithinBorderColor: !readOnly ? 'transparent' : undefined,
		focusWithinRingColor: readOnly ? 'transparent' : undefined
	})

	return (
		<SelectProvider value={context}>
			<SelectBase className={cs('vui-select', className)} ref={ref} {...styles.container} {...aliasedProps} {...rest}>
				{itemLeft}

				{isString(iconLeft) ? <SelectIcon ml={1} name={iconLeft} /> : iconLeft}

				{select ?? (
					<SelectSelect
						ref={selectRef}
						{...{
							autoComplete,
							autoFocus,
							defaultValue,
							disabled,
							id,
							max,
							maxLength,
							min,
							name,
							onBlur,
							onChange,
							onFocus,
							pattern,
							placeholder,
							readOnly,
							required,
							step,
							type,
							value,
							children,
							state
						}}
						{...selectProps}
					/>
				)}

				{isString(iconRight) ? <SelectIcon mr={1} name={iconRight} /> : iconRight}

				{itemRight}

				{state && <SelectIcon mr={1} {...states[state]?.iconProps} />}

				{showCount && (
					<T className="vui-selectCount" color="grey.80" position="absolute" right={0} size="sm" top="calc(100% + 1px)">
						{count} / {maxLength}
					</T>
				)}
			</SelectBase>
		</SelectProvider>
	)
}) as VuiComponent<'div', SelectProps> & {
	Icon: typeof SelectIcon
	Select: typeof SelectSelect
}

Select.displayName = 'Select'

Select.Icon = SelectIcon
Select.Select = SelectSelect

export default Select
